/**
 * ProFind shared text/reason prompt modal — replaces window.prompt() across
 * the theme with an accessible, on-brand dialog. Dependency-free, styles
 * injected once on first use.
 *
 * Usage:
 *   window.ProFindPrompt({
 *     title: 'Decline this Emergency?',
 *     message: 'Optional context for the business.',
 *     fields: [{ name:'reason', label:'Reason (optional)', type:'textarea', required:false, placeholder:'e.g. Out of area' }],
 *     confirmLabel: 'Decline',
 *     danger: true,
 *     onSubmit: function (values) { return someApiCall(values.reason); } // optional
 *   }).then(function (values) {
 *     if (values === null) return; // cancelled
 *     // values.reason etc. — if onSubmit was provided, it has already
 *     // resolved successfully by the time this fires.
 *   });
 *
 * If `onSubmit` is omitted, the promise resolves with the collected field
 * values as soon as Confirm is clicked (validation permitting) and the
 * caller is responsible for its own async handling — matching how the
 * window.prompt() call sites this replaces already worked.
 * If `onSubmit` is provided, the modal shows its own loading state on
 * Confirm, keeps the dialog open and shows an inline error if the promise
 * rejects (with a `.message`), and only resolves/closes once it succeeds —
 * this is what "loading state" / "error state" / "no accidental
 * submission" mean for the call sites that want it.
 */
(function () {
  'use strict';
  if (window.ProFindPrompt) return;

  var injected = false;
  function injectStyles() {
    if (injected) return;
    injected = true;
    var style = document.createElement('style');
    style.textContent =
      '.pfui-prompt-backdrop{position:fixed;inset:0;z-index:10050;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(4,12,28,.62);backdrop-filter:blur(3px);animation:pfuiFade .15s ease}' +
      '@keyframes pfuiFade{from{opacity:0}to{opacity:1}}' +
      '.pfui-prompt-card{width:100%;max-width:440px;max-height:calc(100vh - 40px);overflow:auto;background:linear-gradient(165deg,#0a2042,#071426);border:1px solid rgba(132,190,255,.22);border-radius:18px;padding:26px 24px;color:#fff;box-shadow:0 30px 70px rgba(0,5,18,.45);font-family:inherit}' +
      '.pfui-prompt-title{margin:0 0 6px;font-size:1.15rem;font-weight:850;letter-spacing:-.01em}' +
      '.pfui-prompt-message{margin:0 0 18px;font-size:.83rem;color:#a8bcda;line-height:1.5}' +
      '.pfui-prompt-field{margin-bottom:14px}' +
      '.pfui-prompt-field label{display:block;margin-bottom:6px;font-size:.76rem;font-weight:750;color:#a8bcda}' +
      '.pfui-prompt-field input,.pfui-prompt-field textarea{width:100%;padding:11px 13px;border-radius:10px;border:1.5px solid rgba(132,190,255,.24);background:rgba(255,255,255,.05);color:#fff;font:inherit;font-size:.88rem;box-sizing:border-box;resize:vertical}' +
      '.pfui-prompt-field input:focus,.pfui-prompt-field textarea:focus{outline:none;border-color:#54c9ff;box-shadow:0 0 0 3px rgba(84,201,255,.14)}' +
      '.pfui-prompt-field textarea{min-height:90px}' +
      '.pfui-prompt-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
      '.pfui-prompt-error{display:none;margin:-4px 0 14px;padding:9px 12px;border-radius:9px;background:rgba(239,68,68,.12);border:1px solid rgba(239,68,68,.3);color:#ff9d8a;font-size:.78rem;line-height:1.45}' +
      '.pfui-prompt-error.is-shown{display:block}' +
      '.pfui-prompt-actions{display:flex;justify-content:flex-end;gap:9px;margin-top:6px}' +
      '.pfui-prompt-btn{min-height:42px;padding:0 18px;border-radius:10px;font:inherit;font-size:.84rem;font-weight:800;cursor:pointer;border:1px solid transparent;transition:opacity .15s ease}' +
      '.pfui-prompt-btn:disabled{opacity:.55;cursor:not-allowed}' +
      '.pfui-prompt-btn--cancel{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.14);color:#dbe7f8}' +
      '.pfui-prompt-btn--confirm{background:linear-gradient(135deg,#1687f8,#0b65c7);color:#fff}' +
      '.pfui-prompt-btn--danger{background:linear-gradient(135deg,#ef4444,#b91c1c);color:#fff}' +
      '.pfui-prompt-spinner{display:inline-block;width:13px;height:13px;margin-right:7px;border-radius:50%;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;vertical-align:-2px;animation:pfuiSpin .7s linear infinite}' +
      '@keyframes pfuiSpin{to{transform:rotate(360deg)}}' +
      '@media(max-width:480px){.pfui-prompt-row{grid-template-columns:1fr}.pfui-prompt-card{padding:22px 18px;border-radius:16px}}';
    document.head.appendChild(style);
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  window.ProFindPrompt = function (options) {
    options = options || {};
    // Array.isArray (not truthiness) so an explicit fields:[] means a
    // plain Confirm/Cancel dialog with no input — distinct from omitting
    // `fields` entirely, which falls back to a single reason/value field.
    var fields = Array.isArray(options.fields) ? options.fields : [{ name: 'value', label: options.label || '', type: 'textarea', required: !!options.required, placeholder: options.placeholder || '' }];
    injectStyles();

    return new Promise(function (resolve) {
      var settled = false;
      function settle(value) { if (settled) return; settled = true; cleanup(); resolve(value); }

      var backdrop = document.createElement('div');
      backdrop.className = 'pfui-prompt-backdrop';
      var titleId = 'pfui-prompt-title-' + Math.random().toString(36).slice(2);
      backdrop.setAttribute('role', 'presentation');

      var fieldsHtml = fields.map(function (f) {
        var isArea = f.type === 'textarea';
        var tag = isArea
          ? '<textarea id="pfui-f-' + esc(f.name) + '" name="' + esc(f.name) + '" placeholder="' + esc(f.placeholder || '') + '"' + (f.required ? ' required' : '') + '>' + esc(f.value || '') + '</textarea>'
          : '<input id="pfui-f-' + esc(f.name) + '" name="' + esc(f.name) + '" type="' + esc(f.type || 'text') + '" placeholder="' + esc(f.placeholder || '') + '" value="' + esc(f.value || '') + '"' + (f.required ? ' required' : '') + (f.min ? ' min="' + esc(f.min) + '"' : '') + '>';
        return '<div class="pfui-prompt-field"><label for="pfui-f-' + esc(f.name) + '">' + esc(f.label || '') + '</label>' + tag + '</div>';
      }).join('');

      backdrop.innerHTML =
        '<div class="pfui-prompt-card" role="dialog" aria-modal="true" aria-labelledby="' + titleId + '">' +
          (options.title ? '<h2 class="pfui-prompt-title" id="' + titleId + '">' + esc(options.title) + '</h2>' : '') +
          (options.message ? '<p class="pfui-prompt-message">' + esc(options.message) + '</p>' : '') +
          '<form data-pfui-form>' +
            '<div class="pfui-prompt-error" data-pfui-error></div>' +
            (fields.length > 1 ? '<div class="pfui-prompt-row">' + fieldsHtml + '</div>' : fieldsHtml) +
            '<div class="pfui-prompt-actions">' +
              '<button type="button" class="pfui-prompt-btn pfui-prompt-btn--cancel" data-pfui-cancel>' + esc(options.cancelLabel || 'Cancel') + '</button>' +
              '<button type="submit" class="pfui-prompt-btn ' + (options.danger ? 'pfui-prompt-btn--danger' : 'pfui-prompt-btn--confirm') + '" data-pfui-confirm>' + esc(options.confirmLabel || 'Confirm') + '</button>' +
            '</div>' +
          '</form>' +
        '</div>';

      document.body.appendChild(backdrop);
      var previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      var form = backdrop.querySelector('[data-pfui-form]');
      var cancelBtn = backdrop.querySelector('[data-pfui-cancel]');
      var confirmBtn = backdrop.querySelector('[data-pfui-confirm]');
      var errorEl = backdrop.querySelector('[data-pfui-error]');
      var confirmDefaultLabel = confirmBtn.textContent;
      var firstField = backdrop.querySelector('input, textarea');
      window.setTimeout(function () { (firstField || cancelBtn).focus(); }, 30);

      function cleanup() {
        document.removeEventListener('keydown', onKeydown, true);
        document.body.style.overflow = previousOverflow;
        if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
      }
      function onKeydown(e) {
        if (e.key === 'Escape' && !confirmBtn.disabled) { e.preventDefault(); settle(null); }
      }
      document.addEventListener('keydown', onKeydown, true);

      backdrop.addEventListener('mousedown', function (e) { if (e.target === backdrop && !confirmBtn.disabled) settle(null); });
      cancelBtn.addEventListener('click', function () { if (!confirmBtn.disabled) settle(null); });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (confirmBtn.disabled) return; // no accidental double-submission
        errorEl.className = 'pfui-prompt-error';
        var values = {};
        for (var i = 0; i < fields.length; i++) {
          var f = fields[i];
          var el = document.getElementById('pfui-f-' + f.name);
          var v = el ? el.value.trim() : '';
          if (f.required && !v) {
            errorEl.textContent = 'Please fill in ' + (f.label || 'this field') + '.';
            errorEl.className = 'pfui-prompt-error is-shown';
            if (el) el.focus();
            return;
          }
          values[f.name] = v;
        }
        if (!options.onSubmit) { settle(values); return; }
        confirmBtn.disabled = true;
        cancelBtn.disabled = true;
        confirmBtn.innerHTML = '<span class="pfui-prompt-spinner" aria-hidden="true"></span>' + esc(options.loadingLabel || 'Submitting…');
        options.onSubmit(values).then(function () {
          settle(values);
        }).catch(function (err) {
          confirmBtn.disabled = false;
          cancelBtn.disabled = false;
          confirmBtn.textContent = confirmDefaultLabel;
          errorEl.textContent = (err && err.message) || 'Something went wrong. Please try again.';
          errorEl.className = 'pfui-prompt-error is-shown';
        });
      });
    });
  };
})();
